/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {defaultTreeQueryFunctionFactory, keyCodes, ResultCallback, TrivialComboBox, trivialMatch, TrivialTreeBox} from "trivial-components";

import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {UiTextMatchingMode} from "../../generated/UiTextMatchingMode";
import {UiField} from "./UiField";
import {buildTreeEntryHierarchy, Renderer, TreeEntry} from "../Common";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {
	UiComboBox_LazyChildDataRequestedSubEvent,
	UiComboBoxConfig,
	UiComboBoxSubCommandHandler,
	UiComboBoxSubEventSource
} from "../../generated/UiComboBoxConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {isGridTemplate, isTextCellTemplate} from "../TemplateRegistry";
import {UiTextElementConfig} from "../../generated/UiTextElementConfig";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {UiSpecialKey} from "../../generated/UiSpecialKey";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiComboBox extends UiField<UiComboBoxConfig, any> implements UiComboBoxSubEventSource, UiComboBoxSubCommandHandler {
	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 250);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);
	public readonly onLazyChildDataRequested: TeamAppsEvent<UiComboBox_LazyChildDataRequestedSubEvent> = new TeamAppsEvent<UiComboBox_LazyChildDataRequestedSubEvent>(this);

	private $originalInput: JQuery;
	private trivialComboBox: TrivialComboBox<TreeEntry>;
	private templateRenderers: { [name: string]: Renderer };
	private lastResultCallback: Function;

	protected initialize(config: UiComboBoxConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$originalInput = $(`<input type="text" autocomplete="off">`).appendTo(targetElement);

		this.templateRenderers = context.templateRegistry.createTemplateRenderers(config.templates, config.idPropertyName);

		let entries = config.staticData ? buildTreeEntryHierarchy(config.staticData, config.idPropertyName, config.parentIdPropertyName) : null;

		let localQueryFunction: Function;
		if (config.staticData) {
			let trivialMatchingOptions = UiComboBox.createTrivialMatchingOptions(config);
			localQueryFunction = defaultTreeQueryFunctionFactory(entries, (entry, queryString) => {
				if (config.matchFieldNames) {
					return config.matchFieldNames.some(fieldName => entry[fieldName] && trivialMatch(entry[fieldName], queryString, trivialMatchingOptions).length > 0);
				} else {
					return Object.keys(entry).some(fieldName => entry[fieldName] && trivialMatch(entry[fieldName], queryString, trivialMatchingOptions).length > 0);
				}
			}, "__children", config.expandedProperty);
		}
		let queryFunction = (queryString: string, resultCallback: ResultCallback<TreeEntry>) => {
			this.lastResultCallback = resultCallback;
			this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(queryString));
			if (localQueryFunction != null) {
				localQueryFunction(queryString, resultCallback);
			}
		};

		let emptyEntry: any = config.emptyRecord || {};
		let entryToEditorTextFunction = (entry: TreeEntry) => {
			if (config.editorTextFields && config.editorTextFields.length > 0) {
				return config.editorTextFields.map(editorTextField => entry[editorTextField]).filter(value => value != null).join(" ");
			} else {
				let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
				let renderer = this.getRendererByName(templateName);
				let template = renderer.template;
				if (isTextCellTemplate(template)) {
					return entry[template.textElements[0].fieldName];
				} else if (isGridTemplate(template)) {
					let firstTextElement = template.elements.filter(e => e._type === "UiTextElement")[0] as UiTextElementConfig;
					return entry[firstTextElement.dataKey];
				}
			}
		};
		this.trivialComboBox = new TrivialComboBox(this.$originalInput, {
			allowFreeText: config.allowAnyText,
			entries: entries,
			childrenProperty: "__children",
			expandedProperty: config.expandedProperty,
			showExpanders: config.showExpanders,
			queryFunction: queryFunction,
			entryRenderingFunction: (entry) => {
				let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
				let renderer = this.getRendererByName(templateName);
				return renderer.render(entry);
			},
			selectedEntryRenderingFunction: (entry) => {
				let templateName;
				if (entry == null) {
					entry = emptyEntry;
					templateName = config.emptyTemplateName || config.defaultDisplayTemplateName;
				} else if (entry && entry[config.displayTemplateProperty]) {
					templateName = entry[config.displayTemplateProperty];
				} else {
					templateName = config.defaultDisplayTemplateName;
				}
				let renderer = this.getRendererByName(templateName);
				return renderer.render(entry);
			},
			autoComplete: !!config.autoComplete,
			showTrigger: config.showDropDownButton,
			editingMode: config.editingMode === UiFieldEditingMode.READONLY ? 'readonly' : config.editingMode === UiFieldEditingMode.DISABLED ? 'disabled' : 'editable',
			lazyChildrenQueryFunction: (node) => {
				this.onLazyChildDataRequested.fire(SubEventFactory.createUiComboBox_LazyChildDataRequestedSubEvent(node[this._config.idPropertyName]));
			},
			lazyChildrenFlag: config.lazyChildrenFlagProperty,
			spinnerTemplate: `<div class="UiSpinner" style="height: 20px; width: 20px; margin: 4px auto 4px auto;"></div>`,
			textHighlightingEntryLimit: config.textHighlightingEntryLimit,
			showDropDownOnResultsOnly: config.showDropDownAfterResultsArrive,
			showClearButton: config.showClearButton,
			entryToEditorTextFunction: entryToEditorTextFunction,
			autoCompleteFunction: (editorText, entry) => {
				if (editorText) {
					if (config.matchFieldNames && config.matchFieldNames.length > 0) {
						for (let i = 0; i < config.matchFieldNames.length; i++) {
							const fieldValue = entry[config.matchFieldNames[i]];
							if (fieldValue && fieldValue.toString().toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue.toString();
							}
						}
					} else {
						let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
						let renderer = this.getRendererByName(templateName);
						let template = renderer.template;
						if (isTextCellTemplate(template)) {
							let fieldValue = entry[template.textElements[0].fieldName];
							if (fieldValue && fieldValue.toString().toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue.toString();
							}
						} else if (isGridTemplate(template)) {
							let firstTextElement = template.elements.filter(e => e._type === "UiTextElement")[0] as UiTextElementConfig;
							let fieldValue = entry[firstTextElement.dataKey];
							if (fieldValue && fieldValue.toString().toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue;
							}
						}
						return "";
					}
				} else {
					return entryToEditorTextFunction(entry);
				}
			},
			freeTextEntryFactory: (freeText) => {
				return {
					_isFreeTextEntry: true,
					[this._config.freeTextProperty]: freeText
				};
			},
			inputValueFunction: entry => entry && entry[config.idPropertyName],
			idFunction: entry => entry && entry[config.idPropertyName]
		});
		$(this.trivialComboBox.getMainDomElement()).addClass("UiComboBox");
		$(this.trivialComboBox.getMainDomElement()).attr("data-fieldname", config.fieldName);
		this.trivialComboBox.onSelectedEntryChanged.addListener(() => this.commit());
		$(this.trivialComboBox.getEditor()).on("keydown", (e) => {
			if (e.keyCode === keyCodes.escape) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
			} else if (e.keyCode === keyCodes.enter) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
			}
		});
	}

	isValidData(v: any): boolean {
		return !(typeof v === "string" || typeof v === "number" || typeof v === "boolean");
	}

	private getRendererByName(templateName: string) {
		return this.templateRenderers[templateName] || this._context.templateRegistry.getTemplateRendererByName(templateName);
	}

	static createTrivialMatchingOptions(config: { textMatchingMode?: UiTextMatchingMode }) {
		let matchingModes: { [x: number]: 'contains' | 'prefix' | 'prefix-word' | 'prefix-levenshtein' | 'levenshtein' } = {
			[UiTextMatchingMode.PREFIX]: "prefix",
			[UiTextMatchingMode.PREFIX_WORD]: "prefix-word",
			[UiTextMatchingMode.CONTAINS]: "contains",
			[UiTextMatchingMode.SIMILARITY]: "prefix-levenshtein"
		};
		return {
			matchingMode: matchingModes[config.textMatchingMode],
			ignoreCase: true,
			maxLevenshteinDistance: 3
		};
	}

	setDropDownData(data: any[]): void {
		let hierarchicalData = buildTreeEntryHierarchy(data, this._config.idPropertyName, this._config.parentIdPropertyName);
		(this.lastResultCallback || this.trivialComboBox.updateEntries.bind(this.trivialComboBox))(hierarchicalData);
		if (this._config.showDropDownAfterResultsArrive && data.length > 0 && this.hasFocus()) {
			this.trivialComboBox.openDropDown();
		}
	}

	updateNodes(recordList: any[]): void {
		const topLevelSubNodes = buildTreeEntryHierarchy(recordList, this._config.idPropertyName, this._config.parentIdPropertyName);

		const childrenByParentId: { [parentIdAsString: string]: TreeEntry[] } = {};
		for (let i = 0; i < topLevelSubNodes.length; i++) {
			const subNode = topLevelSubNodes[i];
			let parentIdOrEmptyString = subNode[this._config.parentIdPropertyName] || "";
			if (!childrenByParentId[parentIdOrEmptyString]) {
				childrenByParentId[parentIdOrEmptyString] = [];
			}
			childrenByParentId[parentIdOrEmptyString].push(subNode);
		}

		for (let parentIdAsString in childrenByParentId) {
			let parentId = childrenByParentId[parentIdAsString][0][this._config.parentIdPropertyName];
			(this.trivialComboBox.getDropDownComponent() as TrivialTreeBox<TreeEntry>).updateChildren(parentId, childrenByParentId[parentIdAsString]);
		}
	}

	public getMainDomElement(): JQuery {
		return $(this.trivialComboBox.getMainDomElement());
	}

	public getFocusableElement(): JQuery {
		return $(this.trivialComboBox.getMainDomElement());
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		this.trivialComboBox.setSelectedEntry(uiValue, true);
	}

	public getTransientValue(): any {
		const selectedEntry: TreeEntry = this.trivialComboBox.getSelectedEntry();
		if (selectedEntry != null && selectedEntry._isFreeTextEntry) {
			return {
				_type: this._config.recordTypeId,
				[this._config.freeTextProperty]: selectedEntry[this._config.freeTextProperty]
			};
		} else if (selectedEntry == null) {
			return null;
		} else {
			return selectedEntry;
		}
	}

	focus(): void {
		this.trivialComboBox.focus();
	}

	public hasFocus(): boolean {
		return this.getMainDomElement().is('.focus');
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		this.getMainDomElement()
			.removeClass(Object.keys(UiField.editingModeCssClasses).map((key: any) => UiField.editingModeCssClasses[key]).join(" "))
			.addClass(UiField.editingModeCssClasses[editingMode]);
		if (editingMode === UiFieldEditingMode.READONLY) {
			this.trivialComboBox.setEditingMode("readonly");
		} else if (editingMode === UiFieldEditingMode.DISABLED) {
			this.trivialComboBox.setEditingMode("disabled");
		} else {
			this.trivialComboBox.setEditingMode("editable");
		}
	}

	destroy(): void {
		this.trivialComboBox.destroy();
		this.$originalInput.remove();
	}

	public static getReadOnlyHtml(config: UiComboBoxConfig, value: any, context: TeamAppsUiContext, drawBorder: boolean): string {
		if (value != null) {
			let templateName = value[config.displayTemplateProperty] || config.defaultDisplayTemplateName;
			let renderer = config.templates[templateName] ? context.templateRegistry.createTemplateRenderer(config.templates[templateName], config.idPropertyName)
				: context.templateRegistry.getTemplateRendererByName(templateName);
			return `<div class="static-readonly-UiComboBox ${drawBorder ? "bordered" : ""}">` + renderer.render(value) + '</div>';
		} else {
			return "";
		}
	}

	getDefaultValue(): any {
		return null;
	}

	public valuesChanged(v1: any, v2: any): boolean {
		let nullAndNonNull = ((v1 == null) !== (v2 == null));
		let nonNullAndValuesDifferent = (v1 != null && v2 != null && (
			v1[this._config.idPropertyName] !== v2[this._config.idPropertyName]
			|| v1[this._config.freeTextProperty] !== v2[this._config.freeTextProperty]
		));
		return nullAndNonNull || nonNullAndValuesDifferent;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiComboBox", UiComboBox);